import { FormEvent, ReactNode, useEffect, useState } from "react";
import { isShowMode } from "../showMode";
import { trackAnalyticsEvent } from "../analytics";
import {
  SHOW_PUBLIC_UNLOCK_LABEL,
  clearShowAccessGrant,
  hasShowAccessGrant,
  isShowAccessRequired,
  isShowPublicUnlocked,
  tryGrantShowAccess
} from "../showAccess";

type Props = {
  children: ReactNode;
};

export default function ShowAccessGate({ children }: Props) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [grantedInSession, setGrantedInSession] = useState(false);
  const [autoUnlocked, setAutoUnlocked] = useState(() => isShowPublicUnlocked());

  useEffect(() => {
    if (!isShowMode) return;
    const refreshState = () => {
      const unlocked = isShowPublicUnlocked();
      setAutoUnlocked(unlocked);
      if (unlocked) {
        clearShowAccessGrant();
      }
    };

    refreshState();
    const timer = window.setInterval(refreshState, 30_000);
    return () => window.clearInterval(timer);
  }, []);

  if (!isShowAccessRequired() || autoUnlocked || grantedInSession || hasShowAccessGrant()) {
    return <>{children}</>;
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const ok = tryGrantShowAccess(password);
    if (!ok) {
      setError("Incorrect password.");
      return;
    }
    setError(null);
    setGrantedInSession(true);
    trackAnalyticsEvent("show_unlocked");
    setPassword("");
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: 20
      }}
    >
      <form
        className="card"
        onSubmit={handleSubmit}
        style={{
          width: "min(520px, 100%)",
          display: "grid",
          gap: 12
        }}
      >
        <h2 className="section-title" style={{ margin: 0 }}>
          Access required
        </h2>
        <div className="label">
          This show version is password-protected until {SHOW_PUBLIC_UNLOCK_LABEL}.
        </div>
        <input
          className="input"
          type="password"
          autoComplete="current-password"
          placeholder="Password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        {error && <div className="status-error">{error}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button className="button mobile-full" type="submit" disabled={!password.trim()}>
            Unlock page
          </button>
        </div>
      </form>
    </div>
  );
}
